/**
 * FutureX tier routing (Blueprint v2 §3.3). The single source of truth mapping a
 * {@link Tier} to its model + default effort + allowed-override set + token caps,
 * plus the resolver that ENFORCES the allowed-override set. Shared by the CLI
 * (client-side rejection) and the gateway (authoritative server-side enforcement),
 * so a client can never bypass a tier's caps or allowed-effort set.
 */
import { effortToReasoning, type EffortLevel, type ReasoningOption, type Tier } from './types.js';

export interface TierSpec {
  /** Human-facing mode label. */
  readonly mode: string;
  /**
   * DeepSeek model id. MUST match `@fim/ai-sdk`'s `DEEPSEEK_DEFAULT_MODEL` /
   * `DEEPSEEK_PRO_MODEL` — inlined here so this shared contract stays
   * dependency-free (the SDK constants are the canonical source).
   */
  readonly model: string;
  readonly defaultEffort: EffortLevel;
  readonly allowedEfforts: readonly EffortLevel[];
  /** Hard upper bound on output tokens for the tier (Eco), if any. */
  readonly maxOutputTokens?: number;
}

/** The §3.3 tier table. */
export const TIERS: Readonly<Record<Tier, TierSpec>> = {
  'fx-pro': {
    mode: 'Pro',
    model: 'deepseek-v4-pro',
    defaultEffort: 'max',
    allowedEfforts: ['off', 'high', 'max'],
  },
  'fx-fast': {
    mode: 'Fast',
    model: 'deepseek-v4-flash',
    defaultEffort: 'off',
    allowedEfforts: ['off', 'high'],
  },
  'fx-mini': {
    mode: 'Light',
    model: 'deepseek-v4-flash',
    defaultEffort: 'high',
    allowedEfforts: ['off', 'high', 'max'],
  },
  'fx-eco': {
    mode: 'Eco',
    model: 'deepseek-v4-flash',
    defaultEffort: 'off',
    allowedEfforts: ['off'],
    maxOutputTokens: 4096,
  },
};

/** Default tier when none is selected (Flash-first cost discipline). */
export const DEFAULT_TIER: Tier = 'fx-fast';

/** The tier ids, for help text + validation. */
export const TIER_IDS = Object.keys(TIERS) as Tier[];

/** Narrow an arbitrary string to a {@link Tier}. */
export function isTier(x: string): x is Tier {
  return Object.prototype.hasOwnProperty.call(TIERS, x);
}

const TIER_ALIASES: Readonly<Record<string, Tier>> = {
  'fx-pro': 'fx-pro',
  pro: 'fx-pro',
  'fx-fast': 'fx-fast',
  fast: 'fx-fast',
  'fx-mini': 'fx-mini',
  mini: 'fx-mini',
  light: 'fx-mini',
  'fx-eco': 'fx-eco',
  eco: 'fx-eco',
};

/** Normalize a user-typed module name (an `fx-*` id or a short alias) to a {@link Tier}. */
export function normalizeTier(s: string): Tier | undefined {
  return TIER_ALIASES[s.trim().toLowerCase()];
}

/** Thrown when a per-request effort override is not allowed by the tier. */
export class TierOverrideError extends Error {
  constructor(
    readonly tier: Tier,
    readonly effort: EffortLevel,
    readonly allowed: readonly EffortLevel[],
  ) {
    super(`effort '${effort}' is not allowed for tier '${tier}' (allowed: ${allowed.join(', ')})`);
    this.name = 'TierOverrideError';
  }
}

/** A tier resolved into concrete request parameters. */
export interface ResolvedTier {
  readonly tier: Tier;
  readonly model: string;
  readonly effort: EffortLevel;
  readonly reasoning: ReasoningOption;
  readonly maxTokens?: number;
}

/**
 * Resolve a tier (+ optional effort / maxTokens override) into the concrete
 * model + reasoning + token cap, ENFORCING the tier's allowed-override set.
 * @throws {@link TierOverrideError} when `overrides.effort` is not permitted.
 */
export function resolveTier(
  tier: Tier,
  overrides: { readonly effort?: EffortLevel; readonly maxTokens?: number } = {},
): ResolvedTier {
  const spec = TIERS[tier];
  const effort = overrides.effort ?? spec.defaultEffort;
  if (!spec.allowedEfforts.includes(effort)) {
    throw new TierOverrideError(tier, effort, spec.allowedEfforts);
  }
  // The tier cap is an upper bound; a smaller caller request is honored.
  const cap = spec.maxOutputTokens;
  const maxTokens =
    cap === undefined ? overrides.maxTokens : Math.min(cap, overrides.maxTokens ?? cap);
  return {
    tier,
    model: spec.model,
    effort,
    reasoning: effortToReasoning(effort),
    ...(maxTokens !== undefined ? { maxTokens } : {}),
  };
}
